import { Icon } from './icons'
import { Avatar } from './shared'
import type { Role, Server, User } from './types'

interface SidebarProps {
  route: string
  setRoute: (route: string) => void
  role: Role
  viewer: User | null
  servers: Server[]
  onSignOut: () => void
  open?: boolean
  onNavigate?: () => void
}

interface NavItem {
  id: string
  label: string
  icon: string
  dot?: 'online' | 'warn'
  phase?: string
}

function navDot(server?: Server): 'online' | 'warn' | undefined {
  if (!server) return undefined
  if (server.status === 'online') return 'online'
  if (server.status === 'hibernating' || server.status === 'starting' || server.status === 'stopping' || server.status === 'restarting') return 'warn'
  return undefined
}

export function Sidebar({ route, setRoute, role, viewer, servers, onSignOut, open, onNavigate }: SidebarProps) {
  const items: NavItem[] = [
    { id: 'dashboard', label: 'Dashboard', icon: 'layout-dashboard' },
    ...servers.map(server => ({
      id: `srv:${server.id}`,
      label: server.name,
      icon: 'server',
      dot: navDot(server),
    })),
    { id: 'players', label: 'Players', icon: 'users' },
    { id: 'plugins', label: 'Plugins', icon: 'puzzle' },
    { id: 'backups', label: 'Backups', icon: 'archive' },
    { id: 'audit', label: 'Audit Log', icon: 'history' },
  ]

  if (role === 'owner') {
    items.push({ id: 'settings', label: 'Settings', icon: 'settings' })
  }

  const isActive = (id: string): boolean => {
    if (id === route) return true
    if (id.startsWith('srv:') && route.startsWith(id)) return true
    return false
  }

  return (
    <aside className={`sidebar ${open ? 'open' : ''}`}>
      <div className="brand">
        <div className="brand-logo">M</div>
        <span>mcpanel</span>
      </div>

      <div className="user-pill">
        <Avatar user={viewer} size={26} />
        <div className="meta">
          <span className="name">{viewer?.displayName ?? 'Guest'}</span>
          <span className="role">{role.charAt(0).toUpperCase() + role.slice(1)}</span>
        </div>
      </div>

      {items.map(item => {
        const greyed = Boolean(item.phase)
        const active = isActive(item.id)
        return (
          <a
            key={item.id}
            className={`nav-item ${active ? 'active' : ''} ${greyed ? 'greyed' : ''}`}
            onClick={event => {
              event.preventDefault()
              if (greyed) return
              setRoute(item.id)
              onNavigate?.()
            }}
          >
            <Icon name={item.icon} size={15} className="nav-ico" />
            <span>{item.label}</span>
            {item.dot && <span className={`status-dot ${item.dot === 'online' ? 'online' : 'warn'}`} />}
            {item.phase && <span className="phase-badge">{item.phase}</span>}
          </a>
        )
      })}

      <div className="spacer" />

      <div className="footer">
        v0.3.1 - build abc1234
        <a className="signout" onClick={event => { event.preventDefault(); onSignOut() }}>Sign out</a>
      </div>
    </aside>
  )
}
